/**
 * Clientes — a busca e a ficha de posse.
 *
 * **Esta tela não existe no Sistema antigo.** Hoje, para saber se um cliente
 * tem acesso, alguém abre o PainelAdm. É isso que ela traz para cá, com o
 * recorte que foi pedido: **o que a pessoa possui**, e não o cadastro inteiro.
 *
 * **A pergunta do telefone vem primeiro.** Quem atende quer saber "ele tem
 * acesso agora?", e responder isso lendo seis contratos e cruzando com dois
 * bloqueios é exatamente onde alguém erra. Por isso a ficha abre com o resumo,
 * e a lista de contratos vem depois.
 *
 * **Cada contrato diz se abre, e por que não.** A regra é a de
 * `ContratoDB.PermiteAcesso` e tem quatro partes — validade na data, cota não
 * consumida *contada por contrato*, a licença de demonstração fora do conteúdo
 * comum, e o recorte por categoria de veículo e tipo de informação. As três
 * primeiras cabem numa linha de lista; a quarta depende do conteúdo que a
 * pessoa vai abrir, e a tela diz isso em vez de fingir que sabe.
 *
 * **Dado global**: a trava recusa gravação em desenvolvimento, e as operações
 * avisam antes de deixar clicar.
 */

import { useMemo, useState } from "react"
import {
  cota, data, useBuscaDeClientes, useFichaDoCliente, useLicencas, useOperacaoDeLicenca,
  usarPacotes, usarConferirConcessao, CANAIS,
  type Canal, type ContratoDoCliente, type FichaDoCliente,
} from "@/api/clientes"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { alturaDaJanela } from "@/kit/flutuante"
import { ContatoDoCliente } from "./ContatoDoCliente"
import {
  Aviso, Botao, Campo, ColunaDeLista, ItemDaLista, Linha, Painel, Secao,
  TabelaSimples, Vazio, estiloCampo, mensagem,
} from "../outros/pecas"

const POR_PAGINA = 50

export function Clientes() {
  const [texto, setTexto] = useState("")
  const [busca, setBusca] = useState("")
  const [somenteComLicenca, setSomenteComLicenca] = useState(false)
  const [pagina, setPagina] = useState(1)
  const [aberto, setAberto] = useState<number | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const lista = useBuscaDeClientes(busca, somenteComLicenca, pagina, POR_PAGINA)
  const ficha = useFichaDoCliente(aberto)

  const procurar = () => {
    setBusca(texto)
    setPagina(1)
    setAberto(null)
    setRecado(null)
  }

  const total = lista.data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Clientes" detalhe={busca.length >= 2 && lista.data
        ? `${total.toLocaleString("pt-BR")} encontrados`
        : "152.609 cadastros — procure por nome, CNPJ ou e-mail"}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") procurar() }}
          placeholder="Nome, razão social, CPF/CNPJ ou e-mail"
          style={{ ...estiloCampo, width: 300 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--txt2)", whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={somenteComLicenca}
            onChange={(e) => { setSomenteComLicenca(e.target.checked); setPagina(1) }}
          />
          Só com licença vigente
        </label>
        <Botao aoClicar={procurar} destaque desligado={texto.trim().length < 2}>Procurar</Botao>
      </Faixa>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <ColunaDeLista largura={360}>
          {busca.trim().length < 2 ? (
            <Vazio>
              Digite pelo menos dois caracteres. Com uma letra só a consulta varreria os
              152.609 cadastros e os 165.242 e-mails para devolver meio cadastro.
            </Vazio>
          ) : lista.isLoading ? (
            <Vazio>Procurando…</Vazio>
          ) : lista.isError ? (
            <Vazio>{mensagem(lista.error)}</Vazio>
          ) : total === 0 ? (
            <Vazio>
              Nada com esse texto. A busca cobre nome, razão social, CPF/CNPJ (com ou sem
              pontuação) e <b>todos</b> os e-mails do cliente, não só o principal.
            </Vazio>
          ) : (
            <>
              {(lista.data?.itens ?? []).map((c) => (
                <ItemDaLista
                  key={c.id}
                  ativo={aberto === c.id}
                  aoClicar={() => { setAberto(c.id); setRecado(null) }}
                  marcado={c.licencasVigentes > 0}
                  titulo={
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {c.bloqueado && <Selo cor="erro">bloqueado</Selo>}
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.nome || c.razaoSocial || `Cliente ${c.id}`}
                      </span>
                    </span>
                  }
                  detalhe={
                    <span style={{ fontFamily: "var(--fm)", fontSize: 10.5 }}>
                      {c.cpfCnpj || "sem documento"}
                      {c.licencasVigentes > 0 && ` · ${c.licencasVigentes} licença${c.licencasVigentes > 1 ? "s" : ""}`}
                    </span>
                  }
                />
              ))}

              {paginas > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 10 }}>
                  <Botao aoClicar={() => setPagina((p) => Math.max(1, p - 1))} desligado={pagina <= 1}>‹</Botao>
                  <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
                    {pagina} de {paginas.toLocaleString("pt-BR")}
                  </span>
                  <Botao aoClicar={() => setPagina((p) => Math.min(paginas, p + 1))} desligado={pagina >= paginas}>›</Botao>
                </div>
              )}
            </>
          )}
        </ColunaDeLista>

        <Painel>
          {aberto == null ? (
            <Vazio>
              Escolha um cliente à esquerda. A ficha mostra os contratos com o que cada um
              libera, os produtos em posse perpétua e os bloqueios.
            </Vazio>
          ) : ficha.isLoading ? (
            <Vazio>Abrindo…</Vazio>
          ) : ficha.isError || !ficha.data ? (
            <Vazio>{mensagem(ficha.error)}</Vazio>
          ) : (
            <Ficha ficha={ficha.data} aoAvisar={setRecado} />
          )}
        </Painel>
      </div>

      <BarraDeEstado direita={recado}>
        {busca.trim().length >= 2 && lista.data
          ? `${total.toLocaleString("pt-BR")} cliente(s) · mostrando ${lista.data.itens.length}`
          : "Nenhuma busca feita"}
      </BarraDeEstado>
    </div>
  )
}

// ------------------------------------------------------------------ a ficha

function Ficha({ ficha, aoAvisar }: { ficha: FichaDoCliente; aoAvisar: (s: string | null) => void }) {
  const [operacao, setOperacao] = useState<Operacao | null>(null)

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: "0 0 3px", fontSize: 17, fontWeight: 600 }}>
          {ficha.nome || ficha.razaoSocial || `Cliente ${ficha.id}`}
        </h2>
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt3)", fontFamily: "var(--fm)" }}>
          #{ficha.id} · {ficha.documento || "sem documento"} {ficha.cpfCnpj}
          {ficha.localizacao && ` · ${ficha.localizacao}`}
          {ficha.cadastro && ` · desde ${data(ficha.cadastro)}`}
        </p>
      </div>

      <Resumo ficha={ficha} />

      <Secao titulo="Contratos" contagem={`${ficha.contratos.length} · ${ficha.resumo.contratosVigentes} na validade`}>
        <Aviso>
          <b>"Está válido" não é só a data.</b> A regra que o site aplica confere também a cota
          de visualizações — contada <b>por contrato</b> — e exclui a licença de demonstração do
          conteúdo comum. O alcance por categoria de veículo e tipo de informação depende do
          conteúdo que a pessoa abre, e por isso não cabe nesta lista: veja o plano em Licenças.
        </Aviso>

        <TabelaSimples
          itens={ficha.contratos}
          vazio="Este cliente nunca teve contrato."
          colunas={[
            {
              chave: "id", rotulo: "Abre?", largura: 96,
              render: (c: ContratoDoCliente) =>
                c.daAcesso
                  ? <Selo cor="bom">abre</Selo>
                  : <Selo cor={c.vigente ? "aviso" : "neutro"} titulo={c.porQueNao}>
                      {c.vigente ? "não abre" : "vencido"}
                    </Selo>,
            },
            { chave: "licenca", rotulo: "Licença" },
            {
              chave: "validade", rotulo: "Vence", largura: 116,
              render: (c) => (
                <span title={c.porQueNao || undefined}>
                  {data(c.validade)}
                  {c.vigente && c.diasParaVencer <= 30 && (
                    <span style={{ color: "var(--aviso-on-soft, #7a4b00)" }}> ({c.diasParaVencer}d)</span>
                  )}
                </span>
              ),
            },
            {
              chave: "corteDeAtualizacao", rotulo: "Atualização até", largura: 124,
              render: (c) => data(c.corteDeAtualizacao),
            },
            {
              chave: "visualizacoesUsadas", rotulo: "Visualizações", largura: 190,
              render: (c) => cota(c.visualizacoesUsadas, c.visualizacoesDoPlano, c.semLimite),
            },
            { chave: "origem", rotulo: "Origem", largura: 78 },
            {
              chave: "situacao", rotulo: "", largura: 150,
              render: (c) => (
                <Linha>
                  <Botao aoClicar={() => setOperacao({ tipo: "prorrogar", contrato: c })}>Prorrogar</Botao>
                  <Botao aoClicar={() => setOperacao({ tipo: "corte", contrato: c })}>Corte</Botao>
                </Linha>
              ),
            },
          ]}
        />
        <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--txt3)" }}>
          A coluna <b>situação</b> do banco não aparece aqui de propósito: ela não decide acesso —
          <code> PermiteAcesso</code> não a lê, e entre os 9.266 contratos vigentes exatamente um
          diverge.
        </p>
      </Secao>

      <Secao
        titulo="Produtos"
        contagem={`${ficha.produtos.total} · ${ficha.produtos.noDiagweb} no Diagweb, ${ficha.produtos.noDiag} no Diag, ${ficha.produtos.impressos} impressos`}
      >
        <Aviso>
          Posse <b>perpétua</b>: não tem validade e não gasta visualização. É outra coisa que
          contrato, e por isso um cliente sem contrato vigente ainda pode ter produto.
        </Aviso>
        <TabelaSimples
          itens={ficha.produtos.itens.map((p) => ({ ...p, id: p.produtoId }))}
          vazio="Nenhum produto em posse perpétua."
          colunas={[
            { chave: "produtoId", rotulo: "Id", numero: true, largura: 64 },
            { chave: "produto", rotulo: "Produto" },
            { chave: "origem", rotulo: "Origem", largura: 180, render: (p) => p.origem || "—" },
            { chave: "data", rotulo: "Desde", largura: 96, render: (p) => data(p.data) },
            {
              chave: "diagweb", rotulo: "Canais", largura: 140,
              render: (p) => [p.diagweb && "Diagweb", p.diag && "Diag", p.impresso && "Impresso"]
                .filter(Boolean).join(", ") || "—",
            },
          ]}
        />
      </Secao>

      <Secao titulo="Bloqueios">
        <Bloqueio ficha={ficha} />
      </Secao>

      {/* Contato, dados de acesso e cookies. A lista de e-mails era so leitura aqui
          dentro; agora ela vive junto com telefone e endereco, e da para editar. */}
      <ContatoDoCliente clienteId={ficha.id} aoAvisar={aoAvisar} />

      <Secao titulo="Operações">
        <AvisoDeDadoGlobal oQueMuda="contratos, bloqueios e o histórico do cliente, em produção" />
        <Linha>
          <Botao destaque aoClicar={() => setOperacao({ tipo: "conceder" })}>Conceder licença</Botao>
          {ficha.bloqueio.manual
            ? <Botao aoClicar={() => setOperacao({ tipo: "desbloquear" })}>Desbloquear</Botao>
            : <Botao aoClicar={() => setOperacao({ tipo: "bloquear" })}>Bloquear</Botao>}
          <Botao aoClicar={() => setOperacao({ tipo: "devolver" })}>Devolver visualizações</Botao>
          <Botao aoClicar={() => setOperacao({ tipo: "produtos" })}>Conceder produtos</Botao>
        </Linha>
      </Secao>

      {operacao && (
        <Dialogo
          clienteId={ficha.id}
          operacao={operacao}
          aoFechar={() => setOperacao(null)}
          aoAvisar={aoAvisar}
        />
      )}
    </>
  )
}

/** O cabeçalho que responde "ele tem acesso agora?" antes de qualquer lista. */
function Resumo({ ficha }: { ficha: FichaDoCliente }) {
  const r = ficha.resumo
  const cor = r.temAcessoAgora ? "bom" : ficha.bloqueio.manual ? "erro" : "aviso"

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      padding: "10px 14px", marginBottom: 14, borderRadius: 6,
      background: "var(--surface2)", border: "1px solid var(--line)",
    }}>
      <Selo cor={cor}>{r.temAcessoAgora ? "com acesso" : "sem acesso"}</Selo>
      <span style={{ fontSize: 12.5 }}>{r.situacao}</span>
      {r.venceEm && (
        <span style={{ fontSize: 11.5, color: "var(--txt2)", marginLeft: "auto" }}>
          O mais longo vence em <b>{data(r.venceEm)}</b>
        </span>
      )}
    </div>
  )
}

function Bloqueio({ ficha }: { ficha: FichaDoCliente }) {
  const b = ficha.bloqueio
  if (!b.manual && !b.manualVencido && b.orcamentosBloqueados === 0) {
    return <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--txt3)" }}>Sem bloqueio.</p>
  }

  return (
    <div style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.7 }}>
      {b.manual && (
        <p style={{ margin: 0 }}>
          <Selo cor="erro">bloqueado</Selo>{" "}
          Bloqueio manual até <b>{data(b.manualAte)}</b>. Enquanto ele valer, <b>nenhum
          contrato abre conteúdo</b> — é o primeiro portão que o site confere.
        </p>
      )}
      {!b.manual && b.manualVencido && (
        <p style={{ margin: 0, color: "var(--txt2)" }}>
          Houve um bloqueio, que venceu em {data(b.manualAte)}. Não bloqueia mais nada.
        </p>
      )}
      {b.orcamentosBloqueados > 0 && (
        <p style={{ margin: "4px 0 0" }}>
          <Selo cor="aviso">bloqueio parcial</Selo>{" "}
          {b.orcamentosBloqueados} orçamento(s) com produtos bloqueados
          {" "}(<span style={{ fontFamily: "var(--fm)" }}>{b.codigosDeOrcamento.join(", ")}</span>).
          Isso barra só os produtos vindos daquela nota, não o acesso inteiro.
        </p>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ operações

type Operacao =
  | { tipo: "conceder" }
  | { tipo: "prorrogar"; contrato: ContratoDoCliente }
  | { tipo: "corte"; contrato: ContratoDoCliente }
  | { tipo: "bloquear" }
  | { tipo: "desbloquear" }
  | { tipo: "devolver" }
  | { tipo: "produtos" }

const TITULOS: Record<Operacao["tipo"], string> = {
  conceder: "Conceder licença",
  prorrogar: "Prorrogar contrato",
  corte: "Corte de atualização",
  bloquear: "Bloquear cliente",
  desbloquear: "Desbloquear cliente",
  devolver: "Devolver visualizações",
  produtos: "Conceder produtos",
}

function Dialogo({ clienteId, operacao, aoFechar, aoAvisar }: {
  clienteId: number
  operacao: Operacao
  aoFechar: () => void
  aoAvisar: (s: string | null) => void
}) {
  const ops = useOperacaoDeLicenca(clienteId)
  const licencas = useLicencas()

  const [motivo, setMotivo] = useState("")
  const [licencaId, setLicencaId] = useState(0)
  const [dias, setDias] = useState(30)
  const [dataCorte, setDataCorte] = useState("")
  const [erro, setErro] = useState<string | null>(null)

  // Conceder produtos: escolha, conferência e só então aplicar.
  const [pacotes, setPacotes] = useState<number[]>([])
  const [canais, setCanais] = useState<Canal[]>(["diagweb"])
  const [pedido, setPedido] = useState(0)
  const catalogo = usarPacotes(clienteId)
  const conferencia = usarConferirConcessao(clienteId, pacotes, canais, pedido)

  const escolhida = useMemo(
    () => (licencas.data ?? []).find((l) => l.id === licencaId),
    [licencas.data, licencaId],
  )

  const ocupado = Object.values(ops).some((o) => o.isPending)

  async function confirmar() {
    setErro(null)
    try {
      let r
      switch (operacao.tipo) {
        case "conceder":
          if (licencaId <= 0) { setErro("Escolha a licença."); return }
          r = await ops.conceder.mutateAsync({ licencaId, dias: dias || undefined, motivo })
          break
        case "prorrogar":
          r = await ops.prorrogar.mutateAsync({ contratoId: operacao.contrato.id, dias, motivo })
          break
        case "corte":
          r = await ops.corte.mutateAsync({
            contratoId: operacao.contrato.id,
            data: dataCorte || null,
            motivo,
          })
          break
        case "bloquear":
          r = await ops.bloquear.mutateAsync(motivo)
          break
        case "desbloquear":
          r = await ops.desbloquear.mutateAsync(motivo)
          break
        case "devolver":
          r = await ops.devolverVisualizacoes.mutateAsync(motivo)
          break
        case "produtos":
          if (!conferencia.data) { setErro("Confira antes de aplicar."); return }
          r = await ops.concederProdutos.mutateAsync({ pacoteIds: pacotes, canais, motivo })
          break
      }
      aoAvisar(`${r.operacao}: ${r.resultado}`)
      aoFechar()
    } catch (e) {
      setErro(mensagem(e))
    }
  }

  return (
    <div
      onClick={aoFechar}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 40,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8,
          padding: 18, width: 480, maxWidth: "100%", maxHeight: alturaDaJanela(90), overflowY: "auto",
        }}
      >
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 600 }}>
          {TITULOS[operacao.tipo]}
        </h3>

        {operacao.tipo === "conceder" && (
          <>
            <Campo rotulo="Licença">
              <select
                value={licencaId}
                onChange={(e) => {
                  const id = Number(e.target.value)
                  setLicencaId(id)
                  const l = (licencas.data ?? []).find((x) => x.id === id)
                  if (l) setDias(l.validadeEmDias)
                }}
                style={estiloCampo}
              >
                <option value={0}>Escolha…</option>
                {(licencas.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.titulo} — {l.validadeTexto}
                    {l.semLimite ? "" : `, ${l.visualizacoes.toLocaleString("pt-BR")} visualizações`}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo rotulo="Dias de validade" ajuda="Vazio usa a validade do próprio plano.">
              <input type="number" value={dias} onChange={(e) => setDias(Number(e.target.value))} style={estiloCampo} />
            </Campo>
            {escolhida && (
              <Aviso>
                Vai criar um contrato de <b>cortesia</b> (orçamento zero), válido por {dias} dias
                {escolhida.atualizacaoEmDias > 0 && (
                  <> e com atualização limitada a {Math.min(escolhida.atualizacaoEmDias, dias)} dias</>
                )}.
                Trocar de plano não existe como operação: troca é conceder outro contrato e
                deixar o antigo vencer.
              </Aviso>
            )}
          </>
        )}

        {operacao.tipo === "prorrogar" && (
          <>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--txt2)" }}>
              Contrato #{operacao.contrato.id} — {operacao.contrato.licenca}, vence em{" "}
              <b>{data(operacao.contrato.validade)}</b>.
            </p>
            <Campo rotulo="Dias" ajuda="Soma à validade atual, não a hoje. Negativo antecipa.">
              <input type="number" value={dias} onChange={(e) => setDias(Number(e.target.value))} style={estiloCampo} />
            </Campo>
          </>
        )}

        {operacao.tipo === "corte" && (
          <>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--txt2)" }}>
              Contrato #{operacao.contrato.id} — hoje o corte está em{" "}
              <b>{data(operacao.contrato.corteDeAtualizacao)}</b>.
            </p>
            <Campo rotulo="Nova data" ajuda="Vazio remove o corte e o cliente volta a ver tudo.">
              <input type="date" value={dataCorte} onChange={(e) => setDataCorte(e.target.value)} style={estiloCampo} />
            </Campo>
            <Aviso>
              O corte limita o cliente ao conteúdo publicado até a data. É o que faz o acesso
              vitalício não ser eterno — 8.767 dos 9.266 contratos vigentes têm corte.
            </Aviso>
          </>
        )}

        {operacao.tipo === "bloquear" && (
          <Aviso>
            Bloquear barra <b>tudo</b>: nenhum contrato abre conteúdo enquanto o bloqueio valer.
            É o primeiro portão que o site confere, antes de olhar contrato nenhum.
          </Aviso>
        )}

        {operacao.tipo === "devolver" && (
          <Aviso>
            <b>O nome engana, e vale saber antes de clicar.</b> Esta operação remove o bloqueio e
            tira as visualizações das últimas 24 h dos contadores que o cliente vê. Ela{" "}
            <b>não devolve cota</b>: a cota é contada por contrato, sem recorte de data. Para dar
            cota nova, conceda outro contrato.
          </Aviso>
        )}

        {operacao.tipo === "produtos" && (
          <>
            <Aviso>
              Libera <b>pacotes inteiros</b>, como o Painel — não produto a produto. O que já
              está liberado não é tocado. <b>Não existe a operação inversa aqui</b>: tirar
              produto continua no sistema antigo.
            </Aviso>

            <Campo rotulo="Canais">
              <Linha>
                {CANAIS.map((c) => (
                  <label key={c.chave} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={canais.includes(c.chave)}
                      onChange={(e) => {
                        setPedido(0)
                        setCanais((atual) => e.target.checked
                          ? [...atual, c.chave]
                          : atual.filter((x) => x !== c.chave))
                      }}
                    />
                    {c.rotulo}
                  </label>
                ))}
              </Linha>
            </Campo>

            <Campo rotulo="Pacotes" ajuda="16 dos 26 não têm produto nenhum ligado — liberá-los não faz nada.">
              <div style={{
                maxHeight: 190, overflowY: "auto", border: "1px solid var(--line)",
                borderRadius: 5, padding: 7,
              }}>
                {catalogo.isPending && <p style={{ margin: 0, fontSize: 12, color: "var(--txt3)" }}>Carregando…</p>}
                {(catalogo.data ?? []).map((pc) => (
                  <label key={pc.id} style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0",
                    color: pc.produtos === 0 ? "var(--txt3)" : "var(--txt)",
                  }}>
                    <input
                      type="checkbox"
                      checked={pacotes.includes(pc.id)}
                      onChange={(e) => {
                        setPedido(0)
                        setPacotes((atual) => e.target.checked
                          ? [...atual, pc.id]
                          : atual.filter((x) => x !== pc.id))
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {pc.titulo || `Pacote ${pc.id}`}
                    </span>
                    <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                      {pc.produtos === 0 ? "sem produto" : `${pc.produtos} produto(s)`}
                    </span>
                  </label>
                ))}
              </div>
            </Campo>

            <Linha>
              <Botao
                aoClicar={() => { setErro(null); setPedido((n) => n + 1) }}
                desligado={pacotes.length === 0 || canais.length === 0}
                ocupado={conferencia.isFetching}
              >
                {conferencia.isFetching ? "Conferindo…" : "Conferir o que muda"}
              </Botao>
            </Linha>

            {conferencia.error && (
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--erro-on-soft, #8a1f14)" }}>
                {mensagem(conferencia.error)}
              </p>
            )}

            {conferencia.data && (
              <div style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.65 }}>
                <p style={{ margin: 0 }}>
                  <b>{conferencia.data.produtos}</b> produto(s) de{" "}
                  <b>{conferencia.data.pacotes}</b> pacote(s):{" "}
                  <b>{conferencia.data.entram}</b> posse(s) nascem,{" "}
                  <b>{conferencia.data.ganhamCanal}</b> ganham canal,{" "}
                  {conferencia.data.jaTem} já estão assim.
                </p>
                {conferencia.data.avisos.map((a, i) => (
                  <p key={i} style={{ margin: "6px 0 0", color: "var(--aviso-on-soft, #7a4b00)" }}>{a}</p>
                ))}
                {conferencia.data.entram + conferencia.data.ganhamCanal === 0 && (
                  <p style={{ margin: "6px 0 0", color: "var(--txt2)" }}>
                    Nada a fazer com esta escolha.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        <Campo rotulo="Motivo" ajuda="Vai para o histórico do cliente. É o que responde “quem deu isso, e por quê” seis meses depois.">
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            style={{ ...estiloCampo, resize: "vertical" }}
          />
        </Campo>

        {erro && (
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--erro-on-soft, #8a1f14)" }}>{erro}</p>
        )}

        <Linha>
          <Botao
            destaque
            aoClicar={confirmar}
            desligado={!motivo.trim() || (operacao.tipo === "produtos" &&
              (!conferencia.data ||
               conferencia.data.entram + conferencia.data.ganhamCanal === 0))}
            ocupado={ocupado}
          >
            {operacao.tipo === "produtos" && conferencia.data
              ? `Liberar ${conferencia.data.entram + conferencia.data.ganhamCanal}`
              : "Confirmar"}
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </Linha>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ selo

function Selo({ children, cor, titulo }: {
  children: React.ReactNode
  cor: "bom" | "aviso" | "erro" | "neutro"
  titulo?: string
}) {
  const cores = {
    bom: { fundo: "var(--ok-soft, #e2f0e8)", texto: "var(--ok-on-soft, #14663a)" },
    aviso: { fundo: "var(--aviso-soft, #fdf1d8)", texto: "var(--aviso-on-soft, #7a4b00)" },
    erro: { fundo: "var(--erro-soft, #f8e5e3)", texto: "var(--erro-on-soft, #8a1f14)" },
    neutro: { fundo: "var(--surface2)", texto: "var(--txt3)" },
  }[cor]

  return (
    <span
      title={titulo}
      style={{
        display: "inline-block", padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap",
        fontSize: 10, fontFamily: "var(--fm)", textTransform: "uppercase", letterSpacing: ".04em",
        background: cores.fundo, color: cores.texto,
      }}
    >
      {children}
    </span>
  )
}
